/**
 * Символы раздела «Декор» для развёртки стен: растения, гитары, когтеточки, камин, аквариум
 * и мелочи на столе — спереди и, где это другой рисунок, сбоку.
 *
 * Те же правила, что у мебели: контур в миллиметрах типового предмета, низ — пол (или
 * столешница), сбоку перед смотрит вправо. Ковры на развёртке — полоска толщиной в сантиметр,
 * рисовать там нечего; картины и экран висят на стене и рисуются своими символами.
 */
import { DecorId } from "@/shared/catalog";
import { UIKit, type ElevationSymbols, type SymbolComponent } from "@/shared/ui";
import { Fronts, line, thin } from "../../elevationParts";

/** Растение в кашпо: горшок внизу, крона из крупных листьев */
const PlantFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 1000" {...props}>
		<path d="M450 720V420M450 600L250 380M450 560L660 330M450 480L420 200" {...thin} />
		<ellipse cx="230" cy="350" rx="190" ry="110" transform="rotate(-25 230 350)" {...line} />
		<ellipse cx="680" cy="300" rx="190" ry="110" transform="rotate(25 680 300)" {...line} />
		<ellipse cx="420" cy="160" rx="140" ry="150" {...line} />
		<ellipse cx="560" cy="520" rx="150" ry="80" transform="rotate(15 560 520)" {...line} />
		<ellipse cx="320" cy="560" rx="140" ry="75" transform="rotate(-15 320 560)" {...line} />
		<path d="M280 720H620L580 990H320Z" {...line} strokeLinejoin="round" />
	</UIKit.SymbolFrame>
);

/** Сансевиерия: прямые листья-мечи из горшка */
const SnakePlantFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 900" {...props}>
		<path d="M150 640Q110 300 140 40Q190 300 190 640Z" {...line} />
		<path d="M220 640Q210 260 230 10Q270 260 260 640Z" {...line} />
		<path d="M290 640Q300 330 330 90Q350 330 320 640Z" {...line} />
		<path d="M110 640H340L310 890H140Z" {...line} strokeLinejoin="round" />
	</UIKit.SymbolFrame>
);

/** Пальма: веер длинных листьев из горшка */
const PalmFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1215 1500" {...props}>
		<path d="M607 1150V500" {...line} />
		<path
			d="M607 500Q300 300 40 600M607 500Q900 300 1180 600M607 500Q450 200 300 40M607 500Q760 200 920 40M607 500Q600 250 607 10"
			{...line}
			strokeLinecap="round"
		/>
		<path d="M440 1150H780L740 1490H480Z" {...line} strokeLinejoin="round" />
	</UIKit.SymbolFrame>
);

/** Акустическая гитара на стойке: корпус-восьмёрка с розеткой, гриф и голова */
const GuitarFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 420 1050" {...props}>
		<path d="M110 1040L170 900M310 1040L250 900" {...thin} />
		<rect x="185" y="60" width="50" height="460" {...line} />
		<rect x="170" y="10" width="80" height="70" rx="14" {...line} />
		<path
			d="M210 470C120 470 110 560 140 620C60 650 20 740 40 840C70 960 160 990 210 990C260 990 350 960 380 840C400 740 360 650 280 620C310 560 300 470 210 470Z"
			{...line}
		/>
		<circle cx="210" cy="660" r="55" {...thin} />
		<path d="M160 860H260" {...thin} />
	</UIKit.SymbolFrame>
);

/** Электрогитара на стойке: корпус с рогами, звукосниматели, гриф */
const ElectricGuitarFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 725 1050" {...props}>
		<path d="M260 1040L320 900M470 1040L410 900" {...thin} />
		<rect x="340" y="60" width="45" height="520" {...line} />
		<rect x="325" y="10" width="75" height="70" rx="14" {...line} />
		<path
			d="M300 520Q250 420 200 500Q130 600 160 760Q200 960 362 960Q530 960 570 760Q600 600 520 520Q470 470 430 540Z"
			{...line}
		/>
		<path d="M310 650H415M310 760H415" {...line} />
	</UIKit.SymbolFrame>
);

/** Гитара сбоку: корпус ребром и гриф на стойке */
const GuitarSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 280 1050" {...props}>
		<path d="M40 1040L120 900M240 1040L160 900" {...thin} />
		<rect x="120" y="10" width="40" height="520" rx="12" {...line} />
		<rect x="90" y="470" width="100" height="520" rx="40" {...line} />
	</UIKit.SymbolFrame>
);

/** Когтеточка-домик: столбики, полки, домик и лежанка наверху */
const CatTreeFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 1450" {...props}>
		<rect x="200" y="300" width="70" height="1100" {...thin} />
		<rect x="730" y="250" width="70" height="1150" {...thin} />
		<rect x="40" y="1400" width="920" height="45" rx="10" {...line} />
		<rect x="480" y="950" width="420" height="320" rx="30" {...line} />
		<circle cx="690" cy="1120" r="90" {...thin} />
		<rect x="80" y="760" width="380" height="40" rx="10" {...line} />
		<path d="M560 250Q560 160 760 160Q960 160 960 250Z" {...line} />
		<rect x="560" y="250" width="400" height="40" rx="10" {...line} />
		<rect x="100" y="260" width="300" height="40" rx="10" {...line} />
	</UIKit.SymbolFrame>
);

/** Лежанка для собаки: низкий валик и подушка */
const DogBedFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 800 210" {...props}>
		<rect x="10" y="20" width="780" height="180" rx="80" {...line} />
		<path d="M110 90Q400 150 690 90" {...thin} />
	</UIKit.SymbolFrame>
);

/** Барабанная установка: бочка в центре, томы, малый, тарелки на стойках */
const DrumKitFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1700 1100" {...props}>
		<path
			d="M200 230V1090M1500 180V1090M120 1090L200 980L280 1090M1420 1090L1500 980L1580 1090"
			{...thin}
		/>
		<path d="M30 230H370M1310 180H1690" {...line} strokeLinecap="round" />
		<rect x="300" y="620" width="300" height="160" rx="20" {...line} />
		<rect x="1150" y="560" width="320" height="380" rx="30" {...line} />
		<rect x="620" y="420" width="220" height="170" rx="20" {...line} />
		<rect x="880" y="420" width="220" height="170" rx="20" {...line} />
		<circle cx="860" cy="790" r="290" {...line} />
		<circle cx="860" cy="790" r="80" {...thin} />
	</UIKit.SymbolFrame>
);

/** Мольберт: тренога и холст на полочке */
const EaselFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 765 1700" {...props}>
		<path d="M382 10L80 1690M382 10L685 1690M382 10V1690" {...line} strokeLinecap="round" />
		<rect x="130" y="250" width="505" height="620" {...line} />
		<rect x="110" y="870" width="545" height="40" {...line} />
	</UIKit.SymbolFrame>
);

/** Напольная ваза с сухоцветами */
const VaseFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 540 1100" {...props}>
		<path d="M270 520L120 60M270 520L230 20M270 520L360 40M270 520L470 120" {...thin} />
		<path
			d="M200 500H340Q340 560 400 650Q480 800 420 1090H120Q60 800 140 650Q200 560 200 500Z"
			{...line}
		/>
	</UIKit.SymbolFrame>
);

/** Напольное зеркало: рама с закруглённым верхом */
const MirrorFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 560 1700" {...props}>
		<path d="M20 1690V280Q20 20 280 20Q540 20 540 280V1690Z" {...line} />
		<path d="M60 1650V290Q60 60 280 60Q500 60 500 290V1650Z" {...thin} />
		<path d="M160 500L300 360M200 620L380 440" {...thin} strokeWidth="5" />
	</UIKit.SymbolFrame>
);

/** Камин: каминная полка, портал, топка с аркой и поленьями */
const FireplaceFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 925" {...props}>
		<rect x="60" y="80" width="1080" height="840" {...line} />
		<rect x="10" y="20" width="1180" height="60" {...line} />
		<path d="M300 920V500Q300 300 600 300Q900 300 900 500V920Z" {...line} />
		<path d="M400 880L760 820M440 820L800 880" {...line} strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Корзина для белья: плетёная корзина с ручками */
const LaundryBasketFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 500 420" {...props}>
		<path d="M30 40H470L430 410H70Z" {...line} strokeLinejoin="round" />
		<path d="M45 140H455M55 240H445M65 330H435" {...thin} />
		<rect x="190" y="70" width="120" height="30" rx="14" {...thin} />
	</UIKit.SymbolFrame>
);

/** Аквариум на тумбе: крышка, стекло с водой, рыбка, тумба с дверцами */
const AquariumFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1060 1400" {...props}>
		<rect x="10" y="700" width="1040" height="690" {...line} />
		<Fronts x={10} y={700} width={1040} height={690} count={2} />
		<rect x="20" y="120" width="1020" height="580" {...line} />
		<rect x="10" y="60" width="1040" height="60" rx="10" {...line} />
		<path d="M20 200Q280 170 530 200Q780 230 1040 200" {...thin} />
		<path d="M600 400Q680 340 760 400Q680 460 600 400ZM600 400L560 370V430Z" {...thin} />
		<path d="M200 700Q170 560 220 440M260 700Q300 580 260 480M850 700Q880 600 840 500" {...thin} />
	</UIKit.SymbolFrame>
);

/** Стопка книг лёжа */
const BooksStackFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 300 160" {...props}>
		<rect x="10" y="110" width="280" height="45" {...line} />
		<rect x="30" y="60" width="250" height="50" {...line} />
		<rect x="20" y="10" width="230" height="50" {...line} />
	</UIKit.SymbolFrame>
);

/** Книги в ряд корешками: разной высоты и ширины */
const BooksRowFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 250" {...props}>
		{[
			[10, 30, 50],
			[60, 0, 45],
			[105, 50, 40],
			[145, 20, 60],
			[205, 40, 45],
			[250, 10, 55],
			[305, 60, 40],
			[345, 25, 50],
			[395, 45, 45],
		].map(([x, top, w]) => (
			<rect key={x} x={x} y={top + 5} width={w} height={240 - top} {...line} />
		))}
	</UIKit.SymbolFrame>
);

/** Настольная лампа: основание, изогнутая стойка и плафон */
const DeskLampFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 160 450" {...props}>
		<path d="M80 430V220L120 120" {...line} />
		<path d="M60 120L100 30L150 60L150 150Z" {...line} strokeLinejoin="round" />
		<rect x="20" y="425" width="120" height="22" rx="8" {...line} />
	</UIKit.SymbolFrame>
);

/** Миска для кошки */
const PetBowlFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 160 60" {...props}>
		<path d="M5 8H155L130 55H30Z" {...line} strokeLinejoin="round" />
	</UIKit.SymbolFrame>
);

/** Поилка-фонтанчик: чаша и струйка сверху */
const PetFountainFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 200 180" {...props}>
		<path d="M10 70H190L170 175H30Z" {...line} strokeLinejoin="round" />
		<path d="M100 70V20Q100 10 130 25" {...thin} />
	</UIKit.SymbolFrame>
);

/** Кошачий лоток: низкий поддон с бортиком */
const LitterBoxFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 400 150" {...props}>
		<path d="M10 15H390L370 145H30Z" {...line} strokeLinejoin="round" />
		<path d="M20 50H380" {...thin} />
	</UIKit.SymbolFrame>
);

/** Декоративная подушка: мягкий квадрат с углами-«ушками» */
const CushionFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 450" {...props}>
		<path
			d="M15 15Q225 60 435 15Q390 225 435 435Q225 390 15 435Q60 225 15 15Z"
			{...line}
			strokeLinejoin="round"
		/>
	</UIKit.SymbolFrame>
);

/** Настольная лампа с абажуром: основание, ножка и абажур-трапеция */
const TableLampFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 300 510" {...props}>
		<path d="M70 10H230L290 230H10Z" {...line} strokeLinejoin="round" />
		<path d="M150 230V440" {...line} />
		<path d="M90 500Q90 440 150 440Q210 440 210 500Z" {...line} />
	</UIKit.SymbolFrame>
);

/** Дизайнерская консоль: столешница на плавной изогнутой опоре */
const OrganicConsoleFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 1090" {...props}>
		<rect x="10" y="10" width="1180" height="70" rx="30" {...line} />
		<path
			d="M120 80Q300 400 160 1080H420Q520 600 600 420Q680 600 780 1080H1040Q900 400 1080 80"
			{...line}
			strokeLinejoin="round"
		/>
	</UIKit.SymbolFrame>
);

const PLANT: ElevationSymbols = { front: PlantFront, side: PlantFront };
const GUITAR: ElevationSymbols = { front: GuitarFront, side: GuitarSide };

export const DECOR_ELEVATION_SYMBOLS: Partial<Record<DecorId, ElevationSymbols>> = {
	[DecorId.PlantFloor]: PLANT,
	[DecorId.PlantTable]: PLANT,
	[DecorId.PlantMonstera]: PLANT,
	[DecorId.PlantFicus]: PLANT,
	[DecorId.PlantOlive]: PLANT,
	[DecorId.PlantSnake]: { front: SnakePlantFront, side: SnakePlantFront },
	[DecorId.PlantPalm]: { front: PalmFront, side: PalmFront },
	[DecorId.Guitar]: GUITAR,
	[DecorId.GuitarElectric]: { front: ElectricGuitarFront, side: GuitarSide },
	[DecorId.CatTree]: { front: CatTreeFront },
	[DecorId.DogBed]: { front: DogBedFront, side: DogBedFront },
	[DecorId.DrumKit]: { front: DrumKitFront },
	[DecorId.Easel]: { front: EaselFront, side: EaselFront },
	[DecorId.VaseFloor]: { front: VaseFront, side: VaseFront },
	[DecorId.MirrorFloor]: { front: MirrorFront },
	[DecorId.Fireplace]: { front: FireplaceFront },
	[DecorId.LaundryBasket]: { front: LaundryBasketFront, side: LaundryBasketFront },
	[DecorId.Aquarium]: { front: AquariumFront },
	[DecorId.BooksStack]: { front: BooksStackFront, side: BooksStackFront },
	[DecorId.BooksRow]: { front: BooksRowFront },
	[DecorId.DeskLamp]: { front: DeskLampFront, side: DeskLampFront },
	[DecorId.CushionBaroque]: { front: CushionFront, side: CushionFront },
	[DecorId.CushionPlaid]: { front: CushionFront, side: CushionFront },
	[DecorId.CushionRed]: { front: CushionFront, side: CushionFront },
	[DecorId.CushionRhombus]: { front: CushionFront, side: CushionFront },
	[DecorId.CushionWoven]: { front: CushionFront, side: CushionFront },
	[DecorId.TableLamp]: { front: TableLampFront, side: TableLampFront },
	[DecorId.DeskLampModern]: { front: DeskLampFront, side: DeskLampFront },
	[DecorId.LampWood]: { front: TableLampFront, side: TableLampFront },
	[DecorId.PetBedDonut]: { front: DogBedFront, side: DogBedFront },
	[DecorId.LaundryBasket2]: { front: LaundryBasketFront, side: LaundryBasketFront },
	[DecorId.TableOrganic]: { front: OrganicConsoleFront },
	[DecorId.CatBowl]: { front: PetBowlFront, side: PetBowlFront },
	[DecorId.CatFountain]: { front: PetFountainFront, side: PetFountainFront },
	[DecorId.CatLitter]: { front: LitterBoxFront, side: LitterBoxFront },
};
